import { Component, Input } from '@angular/core';
import { zxcvbn } from '@zxcvbn-ts/core';
import { PASSWORD_MIN_LENGTH } from '../../../core/validators/password.validator';

/**
 * Displays a zxcvbn-based strength bar and a live checklist of password rules.
 *
 * Strength bar: updates in real-time using zxcvbn entropy scoring (0–4).
 *
 * Required rules (hard — form invalid if unmet):
 *   - 12+ characters
 *   - At least 1 letter
 *   - At least 1 number
 *
 * Recommended rules (soft — encouraged but not blocking):
 *   - Upper & lowercase letters
 *   - Special character
 *
 * Usage:
 *   <app-password-hints [password]="formControl.value"></app-password-hints>
 */
@Component({
  selector: 'app-password-hints',
  templateUrl: './password-hints.component.html',
  styleUrls: ['./password-hints.component.scss'],
  standalone: false
})
export class PasswordHintsComponent {
  @Input() password: string | null = '';

  protected readonly minLength = PASSWORD_MIN_LENGTH;

  // zxcvbn score: 0 (very weak) → 4 (very strong)
  get strengthScore(): number {
    const value = this.password || '';
    if (!value) return 0;
    return zxcvbn(value).score;
  }

  // Maps score to an i18n key for the strength label
  get strengthLabelKey(): string {
    const labels = [
      'password_hints.strength_very_weak',
      'password_hints.strength_weak',
      'password_hints.strength_fair',
      'password_hints.strength_strong',
      'password_hints.strength_very_strong'
    ];
    return labels[this.strengthScore];
  }

  // Maps score to a CSS modifier class for the strength bar colour
  get strengthClass(): string {
    return `strength-${this.strengthScore}`;
  }

  get hasMinLength(): boolean {
    return (this.password || '').length >= this.minLength;
  }

  get hasLetter(): boolean {
    return /[a-zA-Z]/.test(this.password || '');
  }

  get hasNumber(): boolean {
    return /[0-9]/.test(this.password || '');
  }

  get hasUpperAndLower(): boolean {
    const value = this.password || '';
    return /[A-Z]/.test(value) && /[a-z]/.test(value);
  }

  get hasSpecialChar(): boolean {
    return /[^a-zA-Z0-9]/.test(this.password || '');
  }
}
